// /Users/pramod/Desktop/school-erp/frontend/src/Tenant-Admin/Layout/TenantAdminLayout.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logoutManager from '../../logout';
import { LogOut, School, User, Building2 } from 'lucide-react';
import { useTenant } from './TenantContext';

const TenantAdminLayout = ({ children }) => {
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();
  const { tenantData, fetchTenantData, loading } = useTenant();

  // Fetch tenant data on mount if not available
  useEffect(() => {
    const userId = localStorage.getItem('userId');
    const tenantId = localStorage.getItem('tenantId');
    
    // If we have userId and tenantId but no tenant data, fetch it
    if (userId && tenantId && !tenantData.institutionName) {
      console.log('🔄 Fetching tenant data on layout mount...');
      fetchTenantData(userId, tenantId);
    }
  }, [fetchTenantData, tenantData.institutionName]);

  const {
    institutionId,
    profileName,
    institutionName,
    logoUrl
  } = tenantData;

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const handleCancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const handleConfirmLogout = async () => {
    setIsLoggingOut(true);
    setShowLogoutConfirm(false);
    
    let redirectPath = '/';

    const result = await logoutManager.logout({
      redirectTo: redirectPath,
      showToast: true,
      onSuccess: () => {
        console.log('✅ Tenant admin logged out successfully');
      }
    });

    if (!result.success) {
      setTimeout(() => {
        navigate(redirectPath);
      }, 500);
    }

    setIsLoggingOut(false);
  };

  const displayName = institutionName && institutionName !== 'Institution Name' && institutionName.trim() !== ''
    ? institutionName 
    : 'Truyon';

  const hasValidLogo = logoUrl && 
                      logoUrl !== 'null' && 
                      logoUrl !== 'undefined' &&
                      logoUrl !== '' &&
                      institutionName && 
                      institutionName !== 'Institution Name' &&
                      institutionName.trim() !== '';

  // Loading state while fetching data
  if (loading && !tenantData.institutionName) {
    return (
      <div className="min-h-screen flex flex-col bg-slate-50">
        {/* Compact Mobile Header - Loading State */}
        <header className="sticky top-0 z-[100] w-full bg-white border-b border-slate-200/80 shadow-xs flex flex-col">
          <div className="h-0.5 w-full bg-gradient-to-r from-indigo-600 to-blue-600 shrink-0" />
          <div className="w-full px-3 h-12 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-indigo-600 to-blue-600 flex items-center justify-center text-white animate-pulse">
                <School size={14} />
              </div>
              <div className="flex flex-col min-w-0 leading-none">
                <div className="h-3 w-20 bg-gray-200 rounded animate-pulse"></div>
                <div className="h-2 w-16 bg-gray-200 rounded mt-1 animate-pulse"></div>
              </div>
            </div>
            <div className="h-7 w-16 bg-gray-200 rounded-lg animate-pulse"></div>
          </div>
        </header>
        <main className="flex-1 p-3 md:p-6 max-w-[1440px] mx-auto w-full">
          {children}
        </main>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen flex flex-col bg-slate-50">
        
        {/* Compact Mobile Header */}
        <header className="sticky top-0 z-[100] w-full bg-white border-b border-slate-200/80 shadow-xs flex flex-col">
          {/* Thin 2px Top Brand Line */}
          <div className="h-0.5 w-full bg-gradient-to-r from-indigo-600 to-blue-600 shrink-0" />

          {/* Core Navigation Bar - Tight h-12 Box */}
          <div className="w-full px-3 h-12 flex items-center justify-between gap-2">
            
            {/* Left Identity Group */}
            <div className="flex items-center gap-2 min-w-0 max-w-[75%]">
              {/* Scaled-down Logo */}
              <div className="shrink-0">
                {hasValidLogo ? (
                  <div className="h-7 w-7 rounded-lg border border-slate-200 p-0.5 bg-white flex items-center justify-center">
                    <img 
                      src={logoUrl} 
                      alt={displayName}
                      className="h-full w-full object-contain rounded-md"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = `
                          <div class="h-full w-full rounded-md bg-indigo-600 flex items-center justify-center text-white text-[10px] font-bold">T</div>
                        `;
                      }}
                    />
                  </div>
                ) : (
                  <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-indigo-600 to-blue-600 flex items-center justify-center text-white shadow-sm">
                    <School size={14} />
                  </div>
                )}
              </div>

              {/* Identity String Labels */}
              <div className="flex flex-col min-w-0 leading-none">
                <h1 className="text-xs font-bold text-slate-900 truncate tracking-tight">
                  {displayName}
                </h1>
                <span className="text-[9px] font-medium text-indigo-600 tracking-wider uppercase mt-0.5">
                  Admin Portal
                </span>
              </div>
            </div>

            {/* Right Action Trigger */}
            <div className="shrink-0 flex items-center gap-1.5">
              {/* Profile Name - Compact */}
              {profileName && profileName !== 'Loading...' && (
                <div className="hidden sm:flex items-center gap-1 text-[10px] text-slate-500 bg-slate-50 px-2 py-1 rounded-lg border border-slate-100">
                  <User size={10} className="text-slate-400" />
                  <span className="font-medium text-slate-700 max-w-[80px] truncate">{profileName}</span>
                </div>
              )}
              
              <button
                onClick={handleLogoutClick}
                disabled={isLoggingOut}
                className="flex items-center justify-center gap-1 h-7 px-2.5 bg-slate-50 hover:bg-red-50 text-slate-700 hover:text-red-600 border border-slate-200 rounded-lg transition-colors disabled:opacity-60 text-[11px] font-semibold"
              >
                {isLoggingOut ? (
                  <svg className="animate-spin h-3 w-3 text-red-600" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" strokeDasharray="32" />
                  </svg>
                ) : (
                  <>
                    <span className="hidden xs:inline">Logout</span>
                    <LogOut size={12} />
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Micro Metadata Sub-strip */}
          {(institutionId || (profileName && profileName !== 'Loading...')) && (
            <div className="border-t border-slate-100 bg-slate-50/50 px-3 py-1 flex items-center justify-between gap-4 text-[10px]">
              {institutionId && (
                <div className="text-slate-500 font-medium flex items-center gap-1 min-w-0">
                  <Building2 size={10} className="text-slate-400 shrink-0" />
                  <p className="truncate">
                    Truyon Institution ID: <span className="text-slate-700 font-semibold">{institutionId}</span>
                  </p>
                </div>
              )}
              {institutionId && profileName && profileName !== 'Loading...' && (
                <div className="text-slate-300 hidden sm:block">•</div>
              )}
              {profileName && profileName !== 'Loading...' && (
                <div className="text-slate-500 font-medium flex items-center gap-1 min-w-0 ml-auto sm:ml-0">
                  <User size={10} className="text-slate-400 shrink-0 sm:hidden" />
                  <p className="truncate sm:hidden">
                    {profileName}
                  </p>
                  <p className="hidden sm:block truncate">
                    Welcome, <span className="text-slate-700 font-semibold">{profileName}</span>
                  </p>
                </div>
              )}
            </div>
          )}
        </header>

        {/* Content Wrapper */}
        <main className="flex-1 p-3 md:p-6 max-w-[1440px] mx-auto w-full">
          {children}
        </main>
      </div>

      {/* Low Profile Modal Overlay */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center z-[1000] p-4">
          <div className="bg-white rounded-xl max-w-xs w-full shadow-lg border border-slate-100 p-4 text-center">
            <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-3">
              <LogOut size={18} className="text-red-600" />
            </div>
            <h3 className="text-sm font-bold text-slate-900 mb-1">
              Confirm Logout
            </h3>
            <p className="text-slate-500 text-[11px] mb-4">
              End your admin session for <span className="font-semibold text-slate-700">{displayName}</span>?
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleCancelLogout}
                className="flex-1 py-1.5 text-[11px] font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmLogout}
                disabled={isLoggingOut}
                className="flex-1 py-1.5 text-[11px] font-bold bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-60"
              >
                {isLoggingOut ? 'Logging out...' : 'Logout'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TenantAdminLayout;